import { useRouter } from 'next/router'
import { PropsWithChildren, useMemo } from 'react'

import { ModalType, useModal } from 'src/providers/ModalProvider'
import { useUser } from 'src/providers/UserProvider'

import { ButtonRebrand } from 'src/components/atoms/ButtonRebrand'
import { Card } from 'src/components/atoms/Card'
import { Heading1 } from 'src/components/atoms/Typography/Headers'
import {
  Action,
  ActionsDropdown
} from 'src/components/molecules/ActionsDropdown'
import { Tabs } from 'src/components/molecules/Tabs'
import { TabData } from 'src/components/molecules/Tabs/Tabs'

import {
  createAccountPath,
  viewAccountBillingDetailsPath,
  viewProfilePath,
  updateAccountPaymentMethodPath,
  taxCertificatesPath,
  listFundingPath
} from 'src/config/paths'
import { env } from 'src/env/client.mjs'
import { ActiveProfileType } from 'src/types/account'
import { Stages } from 'src/types/stages'
import { isHidden } from 'src/utils/account'

export enum FundingHistoryNavTab {
  Billing = 'billing',
  TaxCertificates = 'taxCertificates',
  Transactions = 'transactions'
}

type FundingHistoryNavProps = PropsWithChildren<{
  activeTab: FundingHistoryNavTab
  subscriptionId?: string
}>

export const FundingHistoryNav = ({
  activeTab,
  subscriptionId,
  children
}: FundingHistoryNavProps) => {
  const router = useRouter()
  const { user } = useUser()
  const { openModal } = useModal()

  const isProduction = env.NEXT_PUBLIC_APP_STAGE === Stages.PRODUCTION

  const actions: Action[] = isProduction
    ? []
    : [
        {
          text: 'Add a new account',
          href: createAccountPath
        }
      ]

  if (activeTab === FundingHistoryNavTab.Billing) {
    if (user?.Account?.PaymentMethodDescriptor) {
      actions.unshift({
        text: 'Edit payment method',
        href: updateAccountPaymentMethodPath
      })
    }

    if (subscriptionId) {
      actions.push(
        {
          text: '',
          isSeperator: true
        },
        {
          text: 'Cancel monthly donation',
          onClick: () => openModal(ModalType.CancelSubscription)
        }
      )
    }
  }
  if (user?.Account?.Uri && !isHidden(user?.Account)) {
    actions.push({
      text: 'View public profile',
      href: viewProfilePath({
        routeParams: { uri: user?.Account?.Uri! }
      })
    })
  }

  const tabs = useMemo(() => {
    const availableTabs: TabData[] = []

    if (user?.ActiveProfileType === ActiveProfileType.Funder) {
      availableTabs.push(
        {
          label: 'Transactions',
          value: FundingHistoryNavTab.Transactions,
          path: listFundingPath
        },
        {
          label: 'Payments',
          value: FundingHistoryNavTab.Billing,
          path: viewAccountBillingDetailsPath
        },
        {
          label: 'Tax Certificates',
          value: FundingHistoryNavTab.TaxCertificates,
          path: taxCertificatesPath
        }
      )
    }

    return availableTabs
  }, [user])

  const handleTabChange = (tabValue: string) => {
    if (tabValue === activeTab) {
      return null
    }

    const tab = tabs.find(t => t.value === tabValue)

    if (tab?.path) {
      router.push(tab.path)
    }
  }

  return (
    <>
      <Heading1 className='mb-4'>Funding history</Heading1>
      <Card size='small' className='mb-4'>
        <div className='flex flex-col-reverse md:flex-row items-center gap-5 md:px-5'>
          <Tabs
            activeTab={activeTab}
            ariaLabel='Account navigation'
            onChange={handleTabChange}
            tabs={tabs}
          />
          <div className='place-self-end'>
            {!!actions.length && (
              <ActionsDropdown
                actions={actions}
                ButtonComponent={ButtonRebrand}
                buttonComponentProps={{ variant: 'outline' }}
              >
                Actions
              </ActionsDropdown>
            )}
          </div>
        </div>
        <div className='md:px-5'>{children}</div>
      </Card>
    </>
  )
}
